import { useEffect, useId, useCallback } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { DevTool } from '@hookform/devtools'
import isBoolean from 'lodash/isBoolean'
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import { PrimaryButton, Icon, Toasts, Toast, Spacer } from '@cb/apricot-react'
import { CBInputMethod } from '@cb/apricot'
import { formatDate, DATETIME_FORMATS, isEmpty, Spinner } from '@myap/ui-library'
import { Gender, Nickname, EmailAddress, HomeAddress } from './fields'
import useUserSettingsQuery from '../../../hooks/useUserSettingsQuery'
import useStudentInfoAndRegistrationQuery from '../hooks/useStudentInfoAndRegistrationQuery'
import useGetRestrictionsQuery from '../hooks/useGetRestrictionsQuery'
import useStudentUpdateInformationMutation from '../hooks/useStudentUpdateInformationMutation'
import OpenNewWindow from '../../_common/actions/OpenNewWindow'
import FormErrorMsg from '../../_common/FormErrorMsg'
import { getShadowRootNode } from '../../../utilities/dom'
import { valueOrNull } from '../../../utilities/utils'
import {
  DEFAULT_STUDENT_INFORMATION_VALUES,
  FIELD_NICKNAME,
  FIELD_GENDER,
  FIELD_EMAIL1,
  FIELD_EMAIL2,
  FIELD_STREET1,
  FIELD_STREET2,
  FIELD_STREET3,
  FIELD_CITY,
  FIELD_STATE,
  FIELD_POSTALCODE,
  FIELD_PROVINCE,
  FIELD_COUNTRY,
  FIELD_DOMESTIC_INTL,
  FIELD_SCHOOLNAME,
  FIELD_SCHOOLID,
  FIELD_STUDENTID,
  FIELD_LANG,
  FIELD_GRADE,
  FIELD_PARENT1,
  FIELD_PARENT2,
  FIELD_DESCENDANT_TRIBE,
  FIELD_ENROLLED_TRIBE,
  FIELD_RECOGNIZED_TRIBE,
  FIELD_ETHNICITY,
  FIELD_RACE,
  FIELD_ORG_CITY,
  FIELD_ORG_STATE,
  FIELD_ORG_COUNTRY,
  VALUE_DOMESTIC,
  VALUE_INTERNATIONAL,
  VALUE_FEMALE,
  VALUE_MALE,
  VALUE_ANOTHER_GENDER,
  VALUE_DOMESTIC_COUNTRY,
  ERROR_SERVER,
  ERROR_GENDER,
} from './constants'
import styles from './profileandregform.module.scss'

function ProfileForm({
  changeStep,
  nextStep,
  withHeader = true,
  adjustHeight,
  isReviewMissingInfo = false,
}) {
  const modalHeaderId = useId()
  const isStandAlone = typeof changeStep !== 'function'
  const [showToast, setShowToast] = useState(false)
  const [initialValues, setInitialValues] = useState(null)
  const [finishedInitializing, setFinishedInitializing] = useState(false)
  const [attendingOrgInfo, setAttendingOrgInfo] = useState({})
  const [otherFields, setOtherFields] = useState({})
  const [disabled, setDisabled] = useState(true)
  const countLoadedRestrictions = useRef(0)
  let toastTimerId = null

  // Get user details
  const {
    educationPeriod: { code: educationPeriod, hasEnrollments },
  } = useUserSettingsQuery()

  const refresh = !hasEnrollments && !isStandAlone

  // Send refresh parameter to getStudentInfoAndRegistration when this is student's first
  // enrollment and this is in the enrollment flow
  const variables = { code: educationPeriod, isStudent: true, refresh }

  // Get student info and registration
  const { loading, data } = useStudentInfoAndRegistrationQuery({
    educationPeriod,
    refresh,
  })

  // Mutate student information
  const { mutate: update, loading: isSubmitting } = useStudentUpdateInformationMutation({
    educationPeriod,
    refresh,
  })

  const methods = useForm({
    mode: 'onChange',
    disabled,
    defaultValues: DEFAULT_STUDENT_INFORMATION_VALUES,
    values: initialValues,
  })

  const {
    handleSubmit,
    control,
    formState: { defaultValues, errors, isValid, isDirty },
    watch,
    setError,
  } = methods

  useEffect(() => {
    CBInputMethod()
  }, [])

  useEffect(() => {
    if (!loading && !isEmpty(data)) {
      const keys = data ? Object.keys(data) : []
      let attendingOrgInfoObj = {}
      const initialVals = keys.reduce((obj, key) => {
        if (key !== '__typename') {
          if (key === 'address') {
            const addressData = data.address
            // is addressData an object?
            if (typeof addressData === 'object' && addressData !== null) {
              // get all the keys in address
              const addressKeys = Object.keys(addressData)
              return addressKeys.reduce((obj, addrKey) => {
                if (addrKey !== '__typename' && addrKey !== 'city') {
                  return { ...obj, [addrKey]: addressData[addrKey] || '' }
                }
                // We want to rename the city field so it doesn't clash with ORG CITY
                if (addrKey === 'city') {
                  return { ...obj, [FIELD_CITY]: addressData.city || '' }
                }
                return obj
              }, obj)
            } else {
              // address was not an object, drop in default values
              return {
                ...obj,
                [FIELD_STREET1]: '',
                [FIELD_STREET2]: '',
                [FIELD_STREET3]: '',
                [FIELD_CITY]: '',
                [FIELD_STATE]: '',
                [FIELD_POSTALCODE]: '',
                [FIELD_PROVINCE]: '',
                [FIELD_COUNTRY]: '',
              }
            }
          }
          // attendingOrg can be null when student selects they are not attending a school
          // during account creation
          if (key === 'attendingOrg') {
            const attendingOrgData = data.attendingOrg
            if (attendingOrgData !== null) {
              // get all the keys in attendingOrg
              const attendingOrgKeys = Object.keys(attendingOrgData)
              attendingOrgInfoObj = attendingOrgKeys.reduce((obj, orgKey) => {
                if (orgKey === 'orgName') {
                  return { ...obj, attendingOrgName: attendingOrgData.orgName || '' }
                }
                if (orgKey === 'orgId') {
                  return { ...obj, attendingOrgId: attendingOrgData.orgId || '' }
                }
                // We want to rename the city, stateCd, countryCd fields so they don't clash with fields of same name
                if (orgKey === 'city') {
                  return { ...obj, [FIELD_ORG_CITY]: attendingOrgData.city || '' }
                }
                if (orgKey === 'stateCd') {
                  return { ...obj, [FIELD_ORG_STATE]: attendingOrgData.stateCd || '' }
                }
                if (orgKey === 'countryCd') {
                  return { ...obj, [FIELD_ORG_COUNTRY]: attendingOrgData.countryCd || '' }
                }
                return obj
              }, {})
            }
            // generate empty data when attendingOrg is null
            return {
              ...obj,
              [FIELD_SCHOOLNAME]: '',
              [FIELD_SCHOOLID]: null,
              [FIELD_ORG_CITY]: '',
              [FIELD_ORG_STATE]: '',
              [FIELD_ORG_COUNTRY]: '',
            }
          }
          // if any remaining value is specifically null, convert to empty string
          return {
            ...obj,
            [key]:
              isBoolean(data[key]) || data[key] !== null || Array.isArray(data[key])
                ? data[key]
                : '',
          }
        }
        return obj
      }, {})
      setInitialValues({
        ...DEFAULT_STUDENT_INFORMATION_VALUES,
        [FIELD_NICKNAME]: initialVals[FIELD_NICKNAME] || '',
        [FIELD_EMAIL1]: initialVals[FIELD_EMAIL1],
        [FIELD_STREET1]: initialVals[FIELD_STREET1],
        [FIELD_STREET2]: initialVals[FIELD_STREET2] || '',
        [FIELD_STREET3]: initialVals[FIELD_STREET3] || '',
        [FIELD_CITY]: initialVals[FIELD_CITY],
        [FIELD_STATE]: initialVals[FIELD_STATE] || '',
        [FIELD_POSTALCODE]: initialVals[FIELD_POSTALCODE],
        [FIELD_PROVINCE]: initialVals[FIELD_PROVINCE] || '',
        [FIELD_COUNTRY]: initialVals[FIELD_COUNTRY],
        ...(initialVals[FIELD_COUNTRY] !== VALUE_DOMESTIC_COUNTRY
          ? { [FIELD_DOMESTIC_INTL]: VALUE_INTERNATIONAL }
          : { [FIELD_DOMESTIC_INTL]: VALUE_DOMESTIC }),
        [FIELD_GENDER]:
          initialVals[FIELD_GENDER] !== VALUE_FEMALE &&
          initialVals[FIELD_GENDER] !== VALUE_MALE &&
          initialVals[FIELD_GENDER] !== VALUE_ANOTHER_GENDER
            ? VALUE_ANOTHER_GENDER
            : initialVals[FIELD_GENDER],
      })
      setAttendingOrgInfo(attendingOrgInfoObj)
      setOtherFields({
        studentId: initialVals.studentId,
        dob: initialVals.dob,
        firstName: initialVals.firstName,
        lastName: initialVals.lastName,
        middleInitial: initialVals.middleInitial,
        mobileNumber: initialVals.mobileNumber,
        mobileTextMsgAllowedInd: initialVals.mobileTextMsgAllowedInd,
        [FIELD_STUDENTID]: initialVals[FIELD_STUDENTID],
        [FIELD_LANG]: initialVals[FIELD_LANG],
        [FIELD_GRADE]: initialVals[FIELD_GRADE],
        [FIELD_PARENT1]: initialVals[FIELD_PARENT1],
        [FIELD_PARENT2]: initialVals[FIELD_PARENT2],
        [FIELD_DESCENDANT_TRIBE]: initialVals[FIELD_DESCENDANT_TRIBE],
        [FIELD_RECOGNIZED_TRIBE]: initialVals[FIELD_RECOGNIZED_TRIBE],
        [FIELD_ENROLLED_TRIBE]: initialVals[FIELD_ENROLLED_TRIBE],
        [FIELD_ETHNICITY]: initialVals[FIELD_ETHNICITY],
        [FIELD_RACE]: initialVals[FIELD_RACE],
      })
    }
  }, [data, loading])

  const staticFieldStyles = {
    label: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      fontWeight: 400,
      color: '#1e1e1e',
      marginLeft: 0,
    },
    value: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#1e1e1e',
      marginLeft: 0,
    },
  }

  /** ****************************
   * WATCHES
   ***************************** */
  const email1Val = watch(FIELD_EMAIL1)
  const domesticOrIntVal = watch(FIELD_DOMESTIC_INTL)
  const countryVal = watch(FIELD_COUNTRY)
  const stateVal = watch(FIELD_STATE)
  /** *************************** */

  const confirmationEmailRequired = email1Val !== defaultValues[FIELD_EMAIL1]
  const isDomesticAddress = domesticOrIntVal === VALUE_DOMESTIC
  const isGBOrCA = countryVal === 'GB' || countryVal === 'CA'

  const { data: restrictions, loading: loadingRestrictions } = useGetRestrictionsQuery({
    stateCd: stateVal,
    educationPeriod,
    // skipConditions: !finishedInitializing || VALUE_DOMESTIC_COUNTRY !== countryVal,
    skipConditions: true, // revert to the old values if we end up needing to use this
  })

  const debouncedResize = useCallback(
    debounce(() => {
      if (adjustHeight) {
        adjustHeight()
      }
    }, 250),
    []
  )

  useEffect(() => {
    window.addEventListener('resize', debouncedResize)
    return () => window.removeEventListener('resize', debouncedResize)
  }, [])

  useEffect(() => {
    setDisabled(loading || isSubmitting || loadingRestrictions)
  }, [loading, isSubmitting, loadingRestrictions])

  useEffect(() => {
    if (!isEmpty(restrictions)) {
      countLoadedRestrictions.current += 1
    }
  }, [restrictions])

  useEffect(() => {
    if (initialValues !== null) {
      // The whole purpose for this is to not render the form till all form values have been
      // initialized. Mostly so the school search input will populate with the default value.
      setFinishedInitializing(true)
    }
  }, [initialValues])

  useEffect(() => {
    if (
      !loading &&
      !isEmpty(data) &&
      finishedInitializing &&
      adjustHeight &&
      !loadingRestrictions
    ) {
      adjustHeight()
    }
  }, [
    loading,
    data,
    adjustHeight,
    isDomesticAddress,
    isGBOrCA,
    confirmationEmailRequired,
    finishedInitializing,
    loadingRestrictions,
  ])

  useEffect(() => {
    if (
      !isEmpty(get(errors, `${ERROR_SERVER}.message`)) ||
      !isEmpty(get(errors, `${ERROR_GENDER}.message`))
    ) {
      const errorEl =
        getShadowRootNode().querySelector('#serverError') ??
        getShadowRootNode().querySelector('#invalidGenderError')
      errorEl?.scrollIntoView(true)
      errorEl?.focus()
    }
  }, [get(errors, `${ERROR_SERVER}.message`), get(errors, `${ERROR_GENDER}.message`)])

  const onSubmit = async values => {
    if (isSubmitting) {
      return
    }

    try {
      const {
        [FIELD_EMAIL2]: email2, // remove
        [FIELD_CITY]: city,
        [FIELD_COUNTRY]: countryCd,
        [FIELD_POSTALCODE]: postalCode,
        [FIELD_PROVINCE]: province,
        [FIELD_STATE]: stateCd,
        [FIELD_STREET1]: streetAddr1,
        [FIELD_STREET2]: streetAddr2,
        [FIELD_STREET3]: streetAddr3,
        [FIELD_DOMESTIC_INTL]: domesticIntl, // remove
        ...saveable
      } = values // filter out UI only fields
      const fieldsForProcessing = { ...saveable, ...otherFields }
      // reprocess all values with an empty string as null so service doesn't complain
      const processedSaveable = Object.keys(fieldsForProcessing).reduce((acc, key) => {
        if (fieldsForProcessing[key] === '') {
          return { ...acc, [key]: null }
        }
        return { ...acc, [key]: fieldsForProcessing[key] }
      }, {})
      const updateObj = {
        ...processedSaveable,
        address: {
          streetAddr1,
          streetAddr2: valueOrNull(streetAddr2),
          streetAddr3: valueOrNull(streetAddr3),
          city,
          stateCd: valueOrNull(stateCd),
          postalCode,
          province: valueOrNull(province),
          countryCd,
        },
        attendingOrg: {
          orgName:
            attendingOrgInfo[FIELD_SCHOOLNAME] === '' ? null : attendingOrgInfo[FIELD_SCHOOLNAME],
          orgId: attendingOrgInfo[FIELD_SCHOOLID] === '' ? null : attendingOrgInfo[FIELD_SCHOOLID],
          city: attendingOrgInfo[FIELD_ORG_CITY] === '' ? null : attendingOrgInfo[FIELD_ORG_CITY],
          stateCd:
            attendingOrgInfo[FIELD_ORG_STATE] === '' ? null : attendingOrgInfo[FIELD_ORG_STATE],
          countryCd:
            attendingOrgInfo[FIELD_ORG_COUNTRY] === '' ? null : attendingOrgInfo[FIELD_ORG_COUNTRY],
        },
        attendingOrgId:
          attendingOrgInfo[FIELD_SCHOOLID] === '' ? null : attendingOrgInfo[FIELD_SCHOOLID],
        attendingOrgName: attendingOrgInfo[FIELD_SCHOOLNAME],
      }

      await update({
        variables: {
          ...variables,
          input: updateObj,
          isProfile: true,
          isStudent: true,
        },
      })
      if (!isStandAlone) {
        changeStep(nextStep)
      } else {
        setShowToast(true)
        toastTimerId = setTimeout(() => setShowToast(false), 5000)
      }
    } catch (err) {
      setError('root.serverError', {
        type: 'Server Error',
        message: 'Error saving registration information. Please try again later.',
      })
    }
  }

  if (
    loading ||
    !finishedInitializing ||
    (loadingRestrictions && countLoadedRestrictions.current === 0)
  ) {
    return (
      <div id="modalContent" className="cb-modal-content">
        <Spinner spinnerId="profileFormSpinner" size="64" />
      </div>
    )
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {withHeader ? (
          <div className="cb-modal-header cb-modal-has-close">
            <h2 className="cb-modal-title" id={`apricot_modal_header_${modalHeaderId}`}>
              {isStandAlone ? 'AP Profile' : 'AP Registration - Step 1'}
            </h2>
            <button
              type="button"
              className="cb-btn cb-btn-square cb-btn-greyscale cb-btn-close"
              aria-describedby={`apricot_modal_header_${modalHeaderId}`}
              data-cb-modal-close="true"
            >
              <span />
              <Icon name="x-mark" />
              <span className="sr-only">Close Modal</span>
            </button>
          </div>
        ) : null}
        <div id="modalContent" className="cb-modal-content">
          <div className={styles.registration}>
            <Toasts bottom>
              <Toast
                timeout={5000}
                type="success"
                message="AP Profile saved"
                controlled={true}
                show={showToast}
                onDismiss={() => {
                  clearTimeout(toastTimerId)
                  setShowToast(false)
                }}
              />
            </Toasts>
            {isStandAlone || isReviewMissingInfo ? (
              <p>
                Please confirm the information below for your AP Profile. <strong>NOTE:</strong> If
                changes are needed to your legal name or date of birth, contact AP Services for
                Students by using the{' '}
                <OpenNewWindow url="https://collegeboard.tfaforms.net/193">
                  contact form
                </OpenNewWindow>{' '}
                or calling 888-225-5427. If you make changes to any other information in this
                registration section, you&#39;ll also need to go to your{' '}
                <OpenNewWindow url="https://my.collegeboard.org/profile/information">
                  College Board account settings
                </OpenNewWindow>{' '}
                to make the same change &mdash; changes made to your AP Profile do not automatically
                carry over to your College Board account.
              </p>
            ) : (
              <p>
                Please confirm the information below for your AP Profile. <strong>NOTE:</strong> If
                changes are needed to your legal name or date of birth, contact AP Services for
                Students by using the{' '}
                <OpenNewWindow url="https://collegeboard.tfaforms.net/193">
                  contact form
                </OpenNewWindow>{' '}
                or calling 888-225-5427 <strong>after completing your registration</strong>. If you
                make changes to any other information in this registration section, you&#39;ll also
                need to go to your{' '}
                <OpenNewWindow url="https://my.collegeboard.org/profile/information">
                  College Board account settings
                </OpenNewWindow>{' '}
                to make the same change &mdash; changes made to your AP Profile do not automatically
                carry over to your College Board account.
              </p>
            )}
            <Spacer size="24" />
            <div style={staticFieldStyles.label}>Legal Name</div>
            <div style={staticFieldStyles.value}>{`${data.firstName} ${
              !isEmpty(data.middleInitial) ? `${data.middleInitial} ` : ''
            }${data.lastName}`}</div>
            <Spacer size="16" />
            <Nickname disabled={disabled} />
            <Spacer size="16" />
            <div style={staticFieldStyles.label}>Date of Birth</div>
            <div style={staticFieldStyles.value}>
              {formatDate(data.dob, DATETIME_FORMATS.longMonthDayYear)}
            </div>
            <Spacer size="16" />
            <Gender disabled={disabled} disableThirdOption={restrictions?.gender?.binary} />
            <Spacer size="24" />
            <EmailAddress disabled={disabled} />
            <Spacer size="24" />
            <HomeAddress disabled={disabled} />
            {!isEmpty(get(errors, `${ERROR_SERVER}.message`)) ? (
              <FormErrorMsg error={get(errors, `${ERROR_SERVER}.message`)} id="serverError" />
            ) : null}
            {!isEmpty(get(errors, `${ERROR_GENDER}.message`)) &&
            countLoadedRestrictions.current > 1 ? (
              <FormErrorMsg
                error={get(errors, `${ERROR_GENDER}.message`)}
                id="invalidGenderError"
              />
            ) : null}
          </div>
        </div>
        <div className="cb-modal-footer">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginRight: 'auto' }}>
              <p className="cb-margin-bottom-16">
                Before clicking <strong>{isStandAlone ? 'Save' : 'Next'}</strong>, confirm that all
                information entered on this form is accurate.
              </p>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <PrimaryButton
                type="submit"
                disabled={
                  loadingRestrictions || !isValid || isSubmitting || (!isDirty && isStandAlone)
                }
                loading={isSubmitting}
              >
                {isStandAlone ? 'Save' : 'Next'}
              </PrimaryButton>
            </div>
          </div>
        </div>
        {globalThis?.__DEV__ ? <DevTool control={control} /> : null}
      </form>
    </FormProvider>
  )
}

export default ProfileForm
